import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useSort } from "../hooks/useSort";
import { useDateTimeFormat } from "../utils/formatters";
import { PrimaryButton, GhostButton } from "../components/ThemedButton";

const CAN_MANAGE_ROLES = ["Admin", "Super Admin", "Staff"];

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

const EMPTY_DISCOUNT = {
  name: "", code: "", type: "percentage", value: "", scope: "order", scope_ref_id: "",
  membership_required: false, min_purchase: "", max_discount: "", usage_limit: "",
  per_customer_limit: "", starts_at: "", ends_at: "",
};
const EMPTY_TAX = { location_id: "", name: "", rate_pct: "", is_default: false };

export default function POSDiscounts({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDateTime } = useDateTimeFormat();
  const canManage = CAN_MANAGE_ROLES.includes(currentUser?.user_role);

  const [discounts, setDiscounts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [editingDiscountId, setEditingDiscountId] = useState(null);
  const [discountForm, setDiscountForm] = useState(EMPTY_DISCOUNT);
  const [discountError, setDiscountError] = useState("");
  const [discountBusy, setDiscountBusy] = useState(false);

  const [showTaxForm, setShowTaxForm] = useState(false);
  const [editingTaxId, setEditingTaxId] = useState(null);
  const [taxForm, setTaxForm] = useState(EMPTY_TAX);
  const [taxError, setTaxError] = useState("");
  const [taxBusy, setTaxBusy] = useState(false);

  // Hooks must run unconditionally, before any early return.
  const { sorted: sortedDiscounts, toggleSort: toggleDiscountSort, sortArrow: discountArrow, thSort: discountThSort } =
    useSort(discounts, "name");
  const { sorted: sortedTax, toggleSort: toggleTaxSort, sortArrow: taxArrow, thSort: taxThSort } =
    useSort(taxRates, "name");

  const load = useCallback(() => {
    setError("");
    Promise.allSettled([
      api("/pos/discounts/?active_only=true"),
      api("/pos/tax/rates?active_only=false"),
    ]).then(([d1, d2]) => {
      if (d1.status === "fulfilled") setDiscounts(d1.value.discounts || []);
      else setError(d1.reason.message);
      if (d2.status === "fulfilled") setTaxRates(d2.value.tax_rates || []);
      else setError((e) => e || d2.reason.message);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNewDiscount() {
    setEditingDiscountId(null);
    setDiscountForm(EMPTY_DISCOUNT);
    setDiscountError("");
    setShowDiscountForm(true);
  }

  function openEditDiscount(d) {
    setEditingDiscountId(d.discount_id);
    setDiscountForm({
      name: d.name || "", code: d.code || "", type: d.type || "percentage",
      value: d.value ?? "", scope: d.scope || "order", scope_ref_id: d.scope_ref_id ?? "",
      membership_required: !!d.membership_required, min_purchase: d.min_purchase ?? "",
      max_discount: d.max_discount ?? "", usage_limit: d.usage_limit ?? "",
      per_customer_limit: d.per_customer_limit ?? "",
      starts_at: (d.starts_at || "").slice(0, 16), ends_at: (d.ends_at || "").slice(0, 16),
    });
    setDiscountError("");
    setShowDiscountForm(true);
  }

  async function saveDiscount() {
    if (!discountForm.name.trim() || !discountForm.value) {
      setDiscountError("Name and value are required."); return;
    }
    setDiscountBusy(true); setDiscountError("");
    try {
      const body = JSON.stringify({
        name: discountForm.name.trim(),
        code: discountForm.code || null,
        type: discountForm.type,
        value: parseFloat(discountForm.value),
        scope: discountForm.scope,
        scope_ref_id: discountForm.scope_ref_id ? parseInt(discountForm.scope_ref_id, 10) : null,
        membership_required: discountForm.membership_required,
        min_purchase: discountForm.min_purchase ? parseFloat(discountForm.min_purchase) : null,
        max_discount: discountForm.max_discount ? parseFloat(discountForm.max_discount) : null,
        usage_limit: discountForm.usage_limit ? parseInt(discountForm.usage_limit, 10) : null,
        per_customer_limit: discountForm.per_customer_limit ? parseInt(discountForm.per_customer_limit, 10) : null,
        starts_at: discountForm.starts_at || null,
        ends_at: discountForm.ends_at || null,
      });
      if (editingDiscountId) {
        await api(`/pos/discounts/${editingDiscountId}`, { method: "PUT", body });
      } else {
        await api("/pos/discounts/", { method: "POST", body });
      }
      setDiscountForm(EMPTY_DISCOUNT);
      setEditingDiscountId(null);
      setShowDiscountForm(false);
      load();
    } catch (e) { setDiscountError(e.message); } finally { setDiscountBusy(false); }
  }

  async function deactivateDiscount(id) {
    if (!window.confirm("Deactivate this discount?")) return;
    setBusy(true); setError("");
    try {
      await api(`/pos/discounts/${id}/deactivate`, { method: "POST" });
      load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  function openNewTax() {
    setEditingTaxId(null);
    setTaxForm(EMPTY_TAX);
    setTaxError("");
    setShowTaxForm(true);
  }

  function openEditTax(r) {
    setEditingTaxId(r.tax_rate_id);
    setTaxForm({
      location_id: r.location_id ?? "", name: r.name || "",
      rate_pct: r.rate_pct ?? "", is_default: !!r.is_default,
    });
    setTaxError("");
    setShowTaxForm(true);
  }

  async function saveTaxRate() {
    if (!taxForm.name.trim() || taxForm.rate_pct === "") {
      setTaxError("Name and rate are required."); return;
    }
    setTaxBusy(true); setTaxError("");
    try {
      const body = JSON.stringify({
        location_id: taxForm.location_id ? parseInt(taxForm.location_id, 10) : null,
        name: taxForm.name.trim(),
        rate_pct: parseFloat(taxForm.rate_pct),
        is_default: taxForm.is_default,
      });
      if (editingTaxId) {
        await api(`/pos/tax/rates/${editingTaxId}`, { method: "PUT", body });
      } else {
        await api("/pos/tax/rates", { method: "POST", body });
      }
      setTaxForm(EMPTY_TAX);
      setEditingTaxId(null);
      setShowTaxForm(false);
      load();
    } catch (e) { setTaxError(e.message); } finally { setTaxBusy(false); }
  }

  async function deleteTaxRate(id) {
    if (!window.confirm("Delete this tax code? This cannot be undone.")) return;
    setBusy(true); setError("");
    try {
      await api(`/pos/tax/rates/${id}`, { method: "DELETE" });
      load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function deactivateTaxRate(id) {
    setBusy(true); setError("");
    try {
      await api(`/pos/tax/rates/${id}/deactivate`, { method: "POST" });
      load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function activateTaxRate(id) {
    setBusy(true); setError("");
    try {
      await api(`/pos/tax/rates/${id}/activate`, { method: "POST" });
      load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16, marginBottom: 20 };
  const input = { padding: 8, marginBottom: 10, borderRadius: 8, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, boxSizing: "border-box" };
  const th = { padding: "8px 6px", textAlign: "left", borderBottom: `1px solid ${T.divider}`, color: T.textSub, fontSize: 12 };
  const td = { padding: "8px 6px", borderBottom: `1px solid ${T.divider}`, fontSize: 13 };

  return (
    <div style={{ color: T.textPrimary }}>
      <h2 style={{ marginTop: 0 }}>Tax &amp; Discounts</h2>
      {error && <div style={{ color: T.red, marginBottom: 12 }}>{error}</div>}

      {/* Discounts */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Discount Rules</h3>
          <PrimaryButton
            disabled={!canManage}
            title={canManage ? undefined : "Requires Admin, Super Admin, or Staff role"}
            onClick={() => (showDiscountForm ? setShowDiscountForm(false) : openNewDiscount())}
          >
            {showDiscountForm ? "Cancel" : "+ New Discount"}
          </PrimaryButton>
        </div>

        {showDiscountForm && canManage && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
            {discountError && <div style={{ color: T.red, gridColumn: "1 / -1" }}>{discountError}</div>}
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Name *</label>
              <input style={{ ...input, width: "100%" }} value={discountForm.name}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Coupon Code</label>
              <input style={{ ...input, width: "100%" }} value={discountForm.code}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, code: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Type</label>
              <select style={{ ...input, width: "100%" }} value={discountForm.type}
                      onChange={(e) => setDiscountForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Value *</label>
              <input type="number" style={{ ...input, width: "100%" }} value={discountForm.value}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, value: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Scope</label>
              <select style={{ ...input, width: "100%" }} value={discountForm.scope}
                      onChange={(e) => setDiscountForm((f) => ({ ...f, scope: e.target.value }))}>
                <option value="order">Order</option>
                <option value="product">Product</option>
                <option value="category">Category</option>
              </select>
            </div>
            {discountForm.scope !== "order" && (
              <div>
                <label style={{ fontSize: 11, color: T.textSub }}>Scope Ref ID</label>
                <input type="number" style={{ ...input, width: "100%" }} value={discountForm.scope_ref_id}
                       onChange={(e) => setDiscountForm((f) => ({ ...f, scope_ref_id: e.target.value }))} />
              </div>
            )}
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Min Purchase</label>
              <input type="number" style={{ ...input, width: "100%" }} value={discountForm.min_purchase}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, min_purchase: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Max Discount</label>
              <input type="number" style={{ ...input, width: "100%" }} value={discountForm.max_discount}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, max_discount: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Usage Limit</label>
              <input type="number" style={{ ...input, width: "100%" }} value={discountForm.usage_limit}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, usage_limit: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Per-Customer Limit</label>
              <input type="number" style={{ ...input, width: "100%" }} value={discountForm.per_customer_limit}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, per_customer_limit: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Starts At</label>
              <input type="datetime-local" style={{ ...input, width: "100%" }} value={discountForm.starts_at}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, starts_at: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Ends At</label>
              <input type="datetime-local" style={{ ...input, width: "100%" }} value={discountForm.ends_at}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, ends_at: e.target.value }))} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={discountForm.membership_required}
                     onChange={(e) => setDiscountForm((f) => ({ ...f, membership_required: e.target.checked }))} />
              <label style={{ fontSize: 12 }}>Membership required</label>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <PrimaryButton disabled={discountBusy} onClick={saveDiscount}>
                {discountBusy ? "Saving…" : editingDiscountId ? "Save Changes" : "Create Discount"}
              </PrimaryButton>
            </div>
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, ...discountThSort("name", T) }} onClick={() => toggleDiscountSort("name")}>Name{discountArrow("name")}</th>
                <th style={{ ...th, ...discountThSort("code", T) }} onClick={() => toggleDiscountSort("code")}>Code{discountArrow("code")}</th>
                <th style={{ ...th, ...discountThSort("type", T) }} onClick={() => toggleDiscountSort("type")}>Type{discountArrow("type")}</th>
                <th style={{ ...th, ...discountThSort("value", T) }} onClick={() => toggleDiscountSort("value")}>Value{discountArrow("value")}</th>
                <th style={{ ...th, ...discountThSort("scope", T) }} onClick={() => toggleDiscountSort("scope")}>Scope{discountArrow("scope")}</th>
                <th style={{ ...th, ...discountThSort("times_used", T) }} onClick={() => toggleDiscountSort("times_used")}>Used{discountArrow("times_used")}</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDiscounts.map((d) => (
                <tr key={d.discount_id}>
                  <td style={td}>{d.name}</td>
                  <td style={td}>{d.code || "—"}</td>
                  <td style={{ ...td, textTransform: "capitalize" }}>{d.type}</td>
                  <td style={td}>{d.type === "percentage" ? `${d.value}%` : d.value}</td>
                  <td style={{ ...td, textTransform: "capitalize" }}>{d.scope}</td>
                  <td style={td}>{d.times_used ?? 0}{d.usage_limit ? ` / ${d.usage_limit}` : ""}</td>
                  <td style={{ ...td, display: "flex", gap: 6 }}>
                    <GhostButton size="sm" style={{ opacity: canManage ? 1 : 0.5 }}
                            disabled={!canManage}
                            title={canManage ? undefined : "Requires Admin, Super Admin, or Staff role"}
                            onClick={() => openEditDiscount(d)}>
                      Edit
                    </GhostButton>
                    <GhostButton size="sm" style={{ color: T.red, opacity: canManage ? 1 : 0.5 }}
                            disabled={!canManage || busy}
                            title={canManage ? undefined : "Requires Admin, Super Admin, or Staff role"}
                            onClick={() => deactivateDiscount(d.discount_id)}>
                      Deactivate
                    </GhostButton>
                  </td>
                </tr>
              ))}
              {!sortedDiscounts.length && (
                <tr><td colSpan={7} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No active discounts.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tax Rates */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Tax Rates</h3>
          <PrimaryButton
            disabled={!canManage}
            title={canManage ? undefined : "Requires Admin, Super Admin, or Staff role"}
            onClick={() => (showTaxForm ? setShowTaxForm(false) : openNewTax())}
          >
            {showTaxForm ? "Cancel" : "+ New Tax Code"}
          </PrimaryButton>
        </div>

        {showTaxForm && canManage && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
            {taxError && <div style={{ color: T.red, gridColumn: "1 / -1" }}>{taxError}</div>}
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Name *</label>
              <input style={{ ...input, width: "100%" }} value={taxForm.name}
                     onChange={(e) => setTaxForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Rate % *</label>
              <input type="number" style={{ ...input, width: "100%" }} value={taxForm.rate_pct}
                     onChange={(e) => setTaxForm((f) => ({ ...f, rate_pct: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.textSub }}>Location ID</label>
              <input type="number" style={{ ...input, width: "100%" }} value={taxForm.location_id}
                     onChange={(e) => setTaxForm((f) => ({ ...f, location_id: e.target.value }))} placeholder="Blank = all locations" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={taxForm.is_default}
                     onChange={(e) => setTaxForm((f) => ({ ...f, is_default: e.target.checked }))} />
              <label style={{ fontSize: 12 }}>Default rate</label>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <PrimaryButton disabled={taxBusy} onClick={saveTaxRate}>
                {taxBusy ? "Saving…" : editingTaxId ? "Save Changes" : "Create Tax Code"}
              </PrimaryButton>
            </div>
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, ...taxThSort("name", T) }} onClick={() => toggleTaxSort("name")}>Name{taxArrow("name")}</th>
                <th style={{ ...th, ...taxThSort("rate_pct", T) }} onClick={() => toggleTaxSort("rate_pct")}>Rate{taxArrow("rate_pct")}</th>
                <th style={{ ...th, ...taxThSort("location_id", T) }} onClick={() => toggleTaxSort("location_id")}>Location{taxArrow("location_id")}</th>
                <th style={{ ...th, ...taxThSort("is_default", T) }} onClick={() => toggleTaxSort("is_default")}>Default{taxArrow("is_default")}</th>
                <th style={{ ...th, ...taxThSort("active", T) }} onClick={() => toggleTaxSort("active")}>Status{taxArrow("active")}</th>
                {canManage && <th style={th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedTax.map((r) => (
                <tr key={r.tax_rate_id} style={{ opacity: r.active ? 1 : 0.6 }}>
                  <td style={td}>{r.name}</td>
                  <td style={td}>{r.rate_pct}%</td>
                  <td style={td}>{r.location_id ?? "All"}</td>
                  <td style={td}>{r.is_default ? "Yes" : "No"}</td>
                  <td style={td}>{r.active ? "Active" : "Inactive"}</td>
                  {canManage && (
                    <td style={{ ...td, display: "flex", gap: 6 }}>
                      <GhostButton size="sm" disabled={busy} onClick={() => openEditTax(r)}>Edit</GhostButton>
                      {r.active ? (
                        <GhostButton size="sm" disabled={busy} onClick={() => deactivateTaxRate(r.tax_rate_id)}>Deactivate</GhostButton>
                      ) : (
                        <GhostButton size="sm" disabled={busy} onClick={() => activateTaxRate(r.tax_rate_id)}>Activate</GhostButton>
                      )}
                      <GhostButton size="sm" style={{ color: T.red }} disabled={busy} onClick={() => deleteTaxRate(r.tax_rate_id)}>Delete</GhostButton>
                    </td>
                  )}
                </tr>
              ))}
              {!sortedTax.length && (
                <tr><td colSpan={canManage ? 6 : 5} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No tax codes configured.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
